import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import type { ServeOverrideAction, StateVersionSummary, StateWorkspaceSummary } from '@/lib/apiTypes'
import { useStateVersions, useStateActivity, useStateFindings, useStateLock, useMediaQuery } from '@/lib/hooks'
import { ServeOverrideControl, ServeOverrideBadge } from '@/shared/components/ServeOverrideControl'
import { BlockedBadge } from '@/shared/components/BlockedBadge'
import { api } from '@/lib/api'
import { useToast } from '@/context/ToastContext'
import { WorkspaceProvidersTab } from '@/shared/components/WorkspaceProvidersTab'
import { WorkspaceLockTab } from '@/features/state/tabs/LockTab'
import { formatDateTime, formatDuration, formatCount } from '@/lib/format'
import { PushCadenceChart } from '@/shared/components/PushCadenceChart'
import { Modal } from '@/shared/components/Modal'
import { ConfirmModal } from '@/shared/components/ConfirmModal'
import { Tooltip } from '@/shared/components/Tooltip'
import { PolicyOverrides } from '@/features/policies/components/PolicyOverrides'
import { Badge } from '@/shared/components/Badge'
import { PageIntro } from '@/shared/components/PageIntro'
import { LockIcon } from '@/shared/components/Icons'
import { ScopeNote } from '@/features/state/components/ScopeNote'
import { SlowestAppliesList } from '@/features/state/components/SlowestAppliesList'
import { StatCell } from '@/shared/components/StatCell'
import { WorkspaceRunsTab } from '@/features/state/tabs/RunsTab'
import { SerialsTab } from '@/features/state/tabs/SerialsTab'
import { DETAIL_TABS, type DetailTab } from '@/features/state/tabs/detailTabs'
import { Tabs } from '@/shared/components/Tabs'

export function WorkspaceDetailModal({
  orgId,
  workspace,
  canAdmin,
  onRefresh,
  defaultVersionId,
  onClose,
}: {
  orgId: string
  workspace: StateWorkspaceSummary
  canAdmin: boolean
  onRefresh: () => void
  defaultVersionId?: string
  onClose: () => void
}) {
  const navigate = useNavigate()
  const versions = useStateVersions(orgId, workspace.name)
  const activity = useStateActivity(orgId, workspace.name)
  // Below this the cadence chart's 90 bars and date ticks cannot share a row with the ranked list.
  const narrow = useMediaQuery('(max-width: 900px)')
  const findings = useStateFindings(orgId, workspace.name)
  const lock = useStateLock(orgId, workspace.name)
  const { showToast } = useToast()
  const [selectedVersionId, setSelectedVersionId] = useState<string | null>(defaultVersionId ?? null)
  // A deep link with a versionId (e.g. "View in Findings" from elsewhere) lands on the serial it
  // names rather than the workspace overview.
  const [tab, setTab] = useState<DetailTab>(defaultVersionId ? 'serials' : 'overview')
  const [promoteTarget, setPromoteTarget] = useState<StateVersionSummary | null>(null)
  const [promoting, setPromoting] = useState(false)
  const [overrideBusy, setOverrideBusy] = useState(false)

  // Auto-select the latest version once data loads (or restore from URL param).
  useEffect(() => {
    if (!versions.data?.versions.length) return
    if (selectedVersionId) return
    setSelectedVersionId(versions.data.versions[0]!.id)
  }, [versions.data, selectedVersionId])

  // When defaultVersionId changes (e.g. user navigates to a permalink), sync selection.
  useEffect(() => {
    if (defaultVersionId) setSelectedVersionId(defaultVersionId)
  }, [defaultVersionId])

  const latest = versions.data?.versions[0] ?? null
  const selectedVersion = versions.data?.versions.find((v) => v.id === selectedVersionId) ?? null
  const versionFindings = selectedVersionId
    ? (findings.data?.findings ?? []).filter((f) => f.version_id === selectedVersionId)
    : (findings.data?.findings ?? [])
  const criticalCount = versionFindings.filter((f) => f.severity === 'critical').length

  async function handleServeOverride(action: ServeOverrideAction, reason?: string) {
    setOverrideBusy(true)
    try {
      await api.put(
        `/api/v1/${orgId}/state/${encodeURIComponent(workspace.name)}/serve-override`,
        { action, ...(reason ? { reason } : {}) },
      )
      showToast(action === 'block'
        ? `${workspace.name} force-blocked - the next apply's LOCK will be rejected.`
        : action === 'allow'
          ? `${workspace.name} force-allowed - applies proceed even if policy-blocked.`
          : `${workspace.name} back to Auto - policies decide again.`)
      onRefresh()
    } catch (e) {
      showToast((e as Error).message, 'error')
    } finally {
      setOverrideBusy(false)
    }
  }

  function selectVersion(v: StateVersionSummary) {
    setSelectedVersionId(v.id)
    navigate(`/org/${orgId}/state/${encodeURIComponent(workspace.name)}/${v.id}`, { replace: true })
  }

  async function handlePromote() {
    const target = promoteTarget
    if (!target) return
    setPromoting(true)
    try {
      const res = await api.post<{ serial: number; scan_enqueued: boolean }>(
        `/api/v1/${orgId}/state/${encodeURIComponent(workspace.name)}/versions/${target.id}/promote`,
        // A fresh idempotency key per confirmed promote makes a retried request safe.
        { idempotency_key: crypto.randomUUID() },
      )
      setPromoteTarget(null)
      versions.refetch()
      // A promote writes a new version, so it moves the cadence chart and the totals too.
      activity.refetch()
      findings.refetch()
      onRefresh()
      showToast(`Promoted serial ${target.serial} to new latest (serial ${res.serial}).`, 'success')
      if (!res.scan_enqueued) {
        showToast('Restored, but the scan could not be queued. Use Rescan to scan the restored state.', 'warning')
      }
    } catch (err) {
      const msg = (err as Error).message
      if (/locked|423/i.test(msg)) {
        showToast('Workspace is locked - release the lock and retry.', 'error')
      } else if (/serial_conflict|409/i.test(msg)) {
        showToast('State changed during promote - refresh and retry.', 'error')
      } else {
        showToast(msg, 'error')
      }
    } finally {
      setPromoting(false)
    }
  }

  return (
    <Modal track="state_workspace_detail" title={workspace.name} kicker="// state workspace" maxWidth={1560} onClose={onClose}
      footer={<div style={{ display: 'flex', justifyContent: 'flex-end' }}><button className="btn btn-ghost btn-sm" onClick={onClose}>Close</button></div>}
    >
      {promoteTarget && (
        <ConfirmModal
          track="state_promote_confirm"
          title="Promote version to latest"
          confirmLabel="Promote"
          danger
          busy={promoting}
          message={
            <>
              Promote serial <strong>{promoteTarget.serial}</strong> to become the new latest state? Its
              contents are copied forward as a new version, and the next <code>tofu apply</code> will plan
              against this restored state. Earlier versions are preserved. This is blocked while the workspace
              is locked.
            </>
          }
          onConfirm={handlePromote}
          onCancel={() => setPromoteTarget(null)}
        />
      )}

      {/* Identity row - badges + admin serve-override control, same idiom as the Providers dossier. */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 18px', alignItems: 'center', marginBottom: 16, fontSize: '0.76rem' }}>
        {workspace.latest_serial !== null && (
          <span className="inline-dim">
            latest <code className="mono dim-badge">serial {workspace.latest_serial}</code>
          </span>
        )}
        {lock.data?.lock && (
          <Tooltip text={`Locked by ${lock.data.lock.who} since ${formatDateTime(lock.data.lock.created_at)}`} placement="top">
            <span tabIndex={0} className="cursor-help"><Badge variant="amber">🔒 locked by {lock.data.lock.who}</Badge></span>
          </Tooltip>
        )}
        {workspace.serve_override
          ? <ServeOverrideBadge value={workspace.serve_override} reason={workspace.serve_override_reason} />
          : workspace.blocked === 1 && (
            <BlockedBadge label="✗ blocked" policySummary="An enforcing policy blocks the next apply's LOCK until attested." />
          )}
        <div style={{ flex: 1 }} />
        {canAdmin && (
          <ServeOverrideControl
            value={workspace.serve_override ?? null}
            subject={`workspace ${workspace.name}`}
            busy={overrideBusy}
            compact
            onApply={handleServeOverride}
          />
        )}
      </div>

      {/* Tab bar + active tab content, one container so e2e can scope queries to this
          workspace's panel without matching the WorkspacesTable underneath. */}
      <div data-testid="version-detail-panel">
      {/* The badges are per-key rather than on the registry, because they depend on data this
          component has loaded (the lock, the selected serial's findings) - the registry is static. */}
      <Tabs
        tabs={DETAIL_TABS.map((t) => ({
          ...t,
          badge:
            t.key === 'lock' && lock.data?.lock ? (
              <span className="badge badge-amber" style={{ fontSize: '0.58rem', display: 'inline-flex', alignItems: 'center' }}>
                <LockIcon size={10} />
              </span>
            ) : t.key === 'serials' && selectedVersion && versionFindings.length > 0 ? (
              <span className={`badge badge-${criticalCount > 0 ? 'red' : 'amber'}`} style={{ fontSize: '0.58rem' }} title="Findings on the selected serial">
                {versionFindings.length}
              </span>
            ) : undefined,
        }))}
        value={tab}
        onChange={setTab}
        label="Workspace sections"
      />

      {tab === 'overview' && (
        <>
          <ScopeNote>whole workspace</ScopeNote>
          <PageIntro pageKey="state-workspace-insight" title="What is this tab?">
            <p style={{ margin: 0 }}>
              A KPI snapshot of the <strong>latest version only</strong> (an older version may carry
              different findings - see Serials for the full breakdown), then how often this
              workspace is pushed and which applies are slowest over the last{' '}
              {activity.data?.window_days ?? 90} days
              {activity.data ? ` (${formatCount(activity.data.totals.version_count)} versions in total)` : ''}.
            </p>
          </PageIntro>

          <div className="stat-strip fu" style={{ marginBottom: 16 }}>
            <StatCell
              label="critical · high · latest"
              value={latest ? `${formatCount(latest.critical_count)} · ${formatCount(latest.high_count)}` : '—'}
              color={!latest ? 'var(--text3)' : latest.critical_count > 0 ? 'var(--red)' : latest.high_count > 0 ? 'var(--amber)' : undefined}
            />
            <StatCell label="resources · latest" value={latest ? formatCount(latest.resource_count) : '—'} />
            <StatCell
              label="policy · latest"
              value={latest && (latest.policy_total_count ?? 0) > 0
                ? `${(latest.policy_total_count ?? 0) - (latest.policy_failing_count ?? 0)}/${latest.policy_total_count}`
                : '—'}
              color={latest && (latest.policy_failing_count ?? 0) > 0 ? 'var(--red)' : undefined}
            />
            <StatCell label="runtime · latest" value={latest ? formatDuration(latest.push_duration_ms) : '—'} />
          </div>

          {/* Paired cards, the Dashboard chart-pair idiom. Cadence needs the width for 90 bars
              and its date ticks; the ranked list is naturally narrow. Neither heading says "last
              N versions" any more - both are on the server's time window, and the true lifetime
              total lives in the intro above. */}
          <div style={{ display: 'grid', gridTemplateColumns: narrow ? '1fr' : '1.3fr 1fr', gap: 16, marginBottom: 16 }}>
            <div className="card fu">
              <div className="card-header">
                <div>
                  <div className="kicker">// push cadence</div>
                  <h2 style={{ fontSize: '0.95rem' }}>Pushes · last {activity.data?.window_days ?? 90} days</h2>
                </div>
              </div>
              <div style={{ padding: '16px 18px' }}>
                {activity.loading
                  ? <span className="spinner" />
                  : (
                    <PushCadenceChart
                      pushes={activity.data?.pushes ?? []}
                      windowDays={activity.data?.window_days ?? 90}
                    />
                  )}
              </div>
            </div>

            <div className="card fu">
              <div className="card-header">
                <div>
                  <div className="kicker">// apply runtime</div>
                  <h2 style={{ fontSize: '0.95rem' }}>Slowest applies</h2>
                </div>
              </div>
              <div style={{ padding: '16px 18px' }}>
                {activity.loading ? <span className="spinner" /> : (
                  <>
                    {/* Same 90-day window as the ranking below it, so the strip and the list can
                        never disagree about what the slowest apply was. */}
                    <div className="stat-strip" style={{ marginBottom: 14 }}>
                      <StatCell label="measured applies" value={formatCount(activity.data?.runtime.measured_count ?? 0)} />
                      <StatCell label="median runtime" value={formatDuration(activity.data?.runtime.median_duration_ms)} />
                      <StatCell label="average runtime" value={formatDuration(activity.data?.runtime.avg_duration_ms)} />
                      <StatCell
                        label="slowest runtime"
                        value={formatDuration(activity.data?.runtime.max_duration_ms)}
                        color={(activity.data?.slowest_applies[0]?.critical_count ?? 0) > 0 ? 'var(--red)' : undefined}
                      />
                    </div>
                    <SlowestAppliesList applies={activity.data?.slowest_applies ?? []} />
                  </>
                )}
              </div>
            </div>
          </div>
        </>
      )}

      {tab === 'serials' && (
        <>
          <PageIntro pageKey="state-workspace-versions" title="What is this tab?">
            <p style={{ margin: 0 }}>
              Every pushed version of this workspace's state, newest first. Pick a serial on the left
              and the panel beside it — findings, resources, policy verdicts and the providers that
              version uses — is scoped entirely to it.
            </p>
          </PageIntro>
          <SerialsTab
            orgId={orgId}
            workspace={workspace.name}
            versions={versions.data?.versions ?? []}
            loading={versions.loading}
            selectedVersion={selectedVersion}
            onSelect={selectVersion}
            onPromote={(v) => setPromoteTarget(v)}
            findings={versionFindings}
            findingsLoading={findings.loading || versions.loading}
            lockActive={!!lock.data?.lock}
            allFindings={findings.data?.findings ?? []}
            onMutated={() => { versions.refetch(); findings.refetch(); onRefresh() }}
          />
        </>
      )}

      {tab === 'runs' && (
        <>
          <ScopeNote>whole workspace</ScopeNote>
          <WorkspaceRunsTab orgId={orgId} workspace={workspace.name} />
        </>
      )}

      {tab === 'lock' && (
        <>
          <ScopeNote>whole workspace</ScopeNote>
          <WorkspaceLockTab orgId={orgId} workspace={workspace.name} lock={lock} onUnlocked={onRefresh} />
        </>
      )}

      {tab === 'providers' && (
        <>
          <ScopeNote>whole workspace · as of the last lock file upload</ScopeNote>
          <WorkspaceProvidersTab orgId={orgId} workspace={workspace.name} />
        </>
      )}

      {tab === 'policies' && (
        <>
          <ScopeNote>whole workspace</ScopeNote>
          <PolicyOverrides orgId={orgId} artefactType="state" instanceKey={workspace.id} canAdmin={canAdmin} />
        </>
      )}

      </div>
    </Modal>
  )
}
